/**
 * /premium — dedicated page showcasing every premium-flagged listing.
 * Premium = manual `is_premium=true` on the listing OR price ≥ the
 * `PREMIUM_PRICE_THRESHOLD` setting (default €5000). Category filter
 * chips at the top mirror the homepage so buyers can drill down by
 * industry without leaving the page.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SiteHeader from "@/components/marketplace/SiteHeader";
import SiteFooter from "@/components/marketplace/SiteFooter";
import BullseyeEmbed from "@/components/marketplace/BullseyeEmbed";
import Seo from "@/components/Seo";

export default function PremiumPage() {
  const [items, setItems] = useState([]);
  const [threshold, setThreshold] = useState(5000);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/listings/premium", { params: { limit: 60 } }),
      api.get("/listings/categories"),
    ])
      .then(([p, c]) => {
        setItems(p.data?.items || []);
        setThreshold(p.data?.threshold || 5000);
        setCategories(c.data?.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(
    () => (activeCategory ? items.filter((d) => d.category === activeCategory) : items),
    [items, activeCategory],
  );

  const activeCategories = useMemo(() => {
    const set = new Set(items.map((d) => d.category).filter(Boolean));
    return categories.filter((c) => set.has(c.key));
  }, [categories, items]);

  return (
    <>
      <Seo
        title="Premium domains · CasperDomains"
        description="Category-defining domains for founders who want the front door, not the alley."
      />
      <SiteHeader />
      <main className="cd-container" style={{ paddingTop: 80, paddingBottom: 80, minHeight: "70vh" }}>
        <div style={{ marginBottom: 40 }}>
          <span className="cd-eyebrow" style={{ color: "#92400e" }}>
            Premium · hand-curated
          </span>
          <h1 className="cd-display" style={{ marginTop: 12, fontSize: "clamp(40px, 5vw, 64px)" }} data-testid="premium-page-title">
            The domains I'd buy back myself.
          </h1>
          <p className="cd-lead" style={{ maxWidth: 720, marginTop: 20 }}>
            Names that define a category on day one. Each hand-picked, priced above €{threshold.toLocaleString()},
            and transferable within 24 hours of purchase.
          </p>
        </div>

        {activeCategories.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 40 }} data-testid="premium-category-chips">
            <button
              onClick={() => setActiveCategory("")}
              className="cd-btn cd-btn--sm"
              style={{
                background: activeCategory === "" ? "var(--cd-navy-950)" : "white",
                color: activeCategory === "" ? "white" : "var(--cd-navy-950)",
                border: "1px solid var(--cd-gray-200)",
                fontSize: 11, padding: "6px 14px", borderRadius: 999,
              }}
              data-testid="premium-chip-all"
            >
              All ({items.length})
            </button>
            {activeCategories.map((c) => {
              const n = items.filter((d) => d.category === c.key).length;
              return (
                <button
                  key={c.key}
                  onClick={() => setActiveCategory(c.key === activeCategory ? "" : c.key)}
                  className="cd-btn cd-btn--sm"
                  style={{
                    background: activeCategory === c.key ? "var(--cd-navy-950)" : "white",
                    color: activeCategory === c.key ? "white" : "var(--cd-navy-950)",
                    border: "1px solid var(--cd-gray-200)",
                    fontSize: 11, padding: "6px 14px", borderRadius: 999,
                  }}
                  data-testid={`premium-chip-${c.key}`}
                >
                  {c.key} <span style={{ opacity: 0.55, marginLeft: 4 }}>({n})</span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 80, color: "var(--cd-gray-500)" }}>Loading premium picks…</div>
        ) : visible.length === 0 ? (
          <div
            style={{ textAlign: "center", padding: 80, background: "var(--cd-gray-50)", borderRadius: 18, color: "var(--cd-gray-500)" }}
            data-testid="premium-empty"
          >
            {items.length === 0
              ? `No premium domains yet. Flag any listing as Premium in the admin or set a listing above €${threshold.toLocaleString()} to see it here.`
              : `No premium picks in ${activeCategory} yet.`}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 20,
            }}
            data-testid="premium-grid"
          >
            {visible.map((d) => (
              <Link
                key={d.domain}
                to={`/l/${d.domain}`}
                data-testid={`premium-page-card-${d.domain}`}
                style={{
                  display: "block", padding: "28px 24px",
                  border: "1px solid #fbbf24", borderRadius: 16,
                  background: "linear-gradient(135deg, #fffbeb 0%, #ffffff 65%)",
                  textDecoration: "none", color: "inherit",
                  transition: "transform 200ms ease, box-shadow 200ms ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 16px 32px rgba(146,64,14,0.10)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".1em", color: "#92400e", background: "#fef3c7", padding: "3px 8px", borderRadius: 999, textTransform: "uppercase" }}>
                    ★ Premium
                  </span>
                  {d.category && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: "var(--cd-gray-500)", letterSpacing: ".05em", textTransform: "uppercase" }}>
                      {d.category}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "var(--cd-navy-950)", lineHeight: 1.15, marginBottom: 10, wordBreak: "break-word" }}>
                  {d.domain}
                </div>
                {d.ai_copy?.tagline && (
                  <div style={{ fontSize: 13, color: "var(--cd-gray-600)", marginBottom: 16, lineHeight: 1.45 }}>
                    {d.ai_copy.tagline}
                  </div>
                )}
                {d.premium_story && (
                  <div
                    style={{
                      fontSize: 13, color: "var(--cd-navy-950)", lineHeight: 1.55,
                      marginBottom: 16, paddingLeft: 12,
                      borderLeft: "2px solid #fbbf24",
                      fontStyle: "italic",
                    }}
                    data-testid={`premium-story-${d.domain}`}
                  >
                    {d.premium_story}
                  </div>
                )}
                <div style={{ fontSize: 20, fontWeight: 600, color: "var(--cd-navy-950)" }}>
                  {(d.currency || "EUR") === "EUR" ? "€" : "$"}{(d.price || 0).toLocaleString()}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
      <BullseyeEmbed page="premium" />
    </>
  );
}
